import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ObfuscatedEmail from './ObfuscatedEmail';
import { useLanguagePrefix } from '@/hooks/useLanguagePrefix';

const Footer = () => {
  const { t } = useTranslation();
  const langPrefix = useLanguagePrefix();

  return (
    <footer className="bg-kitco-navy text-white border-t-2 border-kitco-gold/30" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Company Info */}
          <div>
            <img
              src="/logos/kitco-yellow-light-grey.svg"
              alt="Kitco Digital Metals"
              className="h-12 w-auto mb-4"
              data-testid="footer-logo"
            />
            <p className="text-white/80 text-sm leading-relaxed">
              {t('footer.description')}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-4 text-kitco-gold" data-testid="quick-links-header">{t('footer.quickLinks')}</h3>
            <ul className="space-y-2">
              <li>
                <Link to={`${langPrefix}/direct-reserve`} className="text-white hover:text-kitco-gold transition-colors font-medium" data-testid="footer-link-directreserve">
                  {t('nav.directReserve')}
                </Link>
              </li>
              <li>
                <Link to={`${langPrefix}/contact`} className="text-white hover:text-kitco-gold transition-colors font-medium" data-testid="footer-link-contact">
                  {t('nav.contact')}
                </Link>
              </li>
            </ul>
            <div className="mt-4">
              <h4 className="text-sm font-bold mb-2 text-kitco-gold" data-testid="legal-header">{t('footer.legal')}</h4>
              <ul className="space-y-2">
                <li>
                  <a 
                    href="https://www.kitco.com/legal/privacy-policy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white hover:text-kitco-gold transition-colors text-sm"
                    data-testid="privacy-policy-link"
                  >
                    {t('footer.privacyPolicy')}
                  </a>
                </li>
                <li>
                  <a 
                    href="https://www.kitco.com/terms-of-use" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white hover:text-kitco-gold transition-colors text-sm"
                    data-testid="terms-of-use-link"
                  >
                    {t('footer.termsOfService')}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold mb-4 text-kitco-gold" data-testid="contact-info-header">Contact Information</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <Mail className="h-5 w-5 text-kitco-gold mr-2 mt-0.5 flex-shrink-0" />
                <ObfuscatedEmail 
                  user="info" 
                  domain="kitcodigitalmetals.com" 
                  className="text-white hover:text-kitco-gold transition-colors" 
                  data-testid="footer-email"
                />
              </li>
              <li className="flex items-start">
                <Phone className="h-5 w-5 text-kitco-gold mr-2 mt-0.5 flex-shrink-0" />
                <div className="text-white">
                  <a href="tel:18339722424" className="block hover:text-kitco-gold transition-colors" data-testid="footer-phone-us">
                    1-833-972-2424 (USA & Canada)
                  </a>
                  <a href="tel:5148001471" className="block hover:text-kitco-gold transition-colors" data-testid="footer-phone-intl">
                    514-800-1471 (Worldwide)
                  </a>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-kitco-gold/30 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/70 text-sm" data-testid="copyright-text">
              © {new Date().getFullYear()} Kitco Digital Metals. {t('footer.rights')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;